import { assertResolvedAtsNotHttpError } from '@/api/ats/atsEnvelope';
import { useQuery } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import type { SharedCalendarShowSettingResponse } from './useGetSharedCalendar.interface';

export const sharedCalendarSettingQueryKey = [
  'shared-calendars',
  'show_setting',
] as const;

export async function fetchSharedCalendarSetting(): Promise<boolean> {
  const raw = await http.get('/shared_calendars/show_setting');
  assertResolvedAtsNotHttpError(raw);
  const res = raw as unknown as SharedCalendarShowSettingResponse;
  const errs = res?.errors ?? [];
  if (errs.length > 0) {
    throw new Error(
      errs.join(', ') || 'Failed to load shared calendar settings'
    );
  }
  return Boolean(res?.data?.shared_calendar_enabled);
}

/**
 * Whether shared calendars are enabled for the account (`GET …/shared_calendars/show_setting`).
 * Use this to gate {@link useGetSharedCalendar} and hide calendar UI when disabled.
 *
 * Pass `{ enabled: false }` to skip the request (e.g. plan/edit flows that
 * already carry `shared_calendar_enabled` on the response and only adhoc
 * needs the account-level fetch).
 */
export function useSharedCalendarSetting(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: sharedCalendarSettingQueryKey,
    queryFn: fetchSharedCalendarSetting,
    enabled: options?.enabled ?? true,
    staleTime: 0,
  });
}
